/**
 * The destination (output) settings screen's data contract.
 *
 * The counterpart to `feed-settings-sections.ts`: it turns the social record
 * `SocialEditor.jsx` holds into the `SettingsSection[]` the redesigned screen
 * takes, with every field carrying its legacy `data-field` dot path in `name`.
 *
 * WHERE THE SHAPE COMES FROM. The tab set is `SocialConfigButtons`'; the
 * fields are `SocialPanelModule`'s shared getters plus the 21 `*Options.jsx`
 * panels the `switch (psID)` in `SocialEditorPanel` dispatches to. This module
 * is the per-network spec data that switch becomes — the same move
 * `social-input-configure.ts` already made for the create-automation wizard.
 *
 * THE TAB SET RESOLVES TO THREE OUTCOMES, NOT THREE TABS: X / LinkedIn /
 * StockTwits get Post options + Links + Advanced, Pinterest / TikTok / YouTube
 * get Post options alone, everything else gets Post options + Links. The shell
 * hides the strip when only one section survives.
 */

import {
  type SettingsField,
  type SettingsOption,
  type SettingsSection,
} from "@/shadcn/components/settings/settings-form"
import {
  NETWORK_PSID,
  type DestinationNetwork,
} from "@/shadcn/components/settings/settings-vocab"

import {
  type Constants,
  type LegacyRecord,
  activeLast,
  affixHashtagFault,
  attachErrors,
  choice,
  flag,
  options,
  readPath,
  text,
} from "@/lib/settings-sections"

export type SocialSectionsContext = {
  constants: Constants
  /** `app.config.feedUrl`, for the Output Feed's own address. */
  feedUrlBase: string
  /** `state.shorteners`, for the Links tab's one select. */
  shorteners: LegacyRecord[]
  /** `state.domains`, which share that select with the shorteners. */
  domains: LegacyRecord[]
  /**
   * `ConstantMixin.getShortenServiceByCode`. Passed rather than imported so
   * this module stays free of the legacy mixins, the way
   * `social-input-configure.ts` does with `constants`.
   */
  shortenServiceName: (ssID: number) => string
  /** `state.socials.categories` — YouTube's, keyed by social id. */
  categories: LegacyRecord
  /** `state.socials.playlists` — likewise. */
  playlists: LegacyRecord
  /** `validationTree(app)` — the server's complaint about the last write. */
  validation?: LegacyRecord | null
  /** The advanced view, where a route can choose a shortener of its own. */
  advancedView?: boolean
}

/* ─────────────────────────────────────────────────────────── vocabulary ── */

const PSID_NETWORK: Record<number, DestinationNetwork> = Object.fromEntries(
  Object.entries(NETWORK_PSID).map(([network, psID]) => [psID, network])
) as Record<number, DestinationNetwork>

/** `POST_SVC_*` -> the key the screens use. A lookup, not a switch. */
export function destinationNetwork(
  social: LegacyRecord | null | undefined
): DestinationNetwork | undefined {
  return PSID_NETWORK[parseInt(String(social?.psID ?? ""), 10)]
}

const SECTION_LABEL: Record<string, string> = {
  "post-options": "Post options",
  links: "Links",
  advanced: "Advanced",
}

/** Only these three expose an Advanced tab — `SocialConfigButtons`' own list. */
const HAS_ADVANCED: DestinationNetwork[] = ["twitter", "linkedin", "stocktwits"]

/** These three get Post options and nothing else. */
const POST_OPTIONS_ONLY: DestinationNetwork[] = ["pinterest", "tiktok", "youtube"]

/** The networks whose post can be a link card or a plain status. */
/*
 * DISCORD AND SLACK ARE BOTH ABSENT, and for the same reason: neither renders a
 * style picker in legacy. Slack's was removed deliberately (`629faa026`,
 * "Remove Shared Link/Status Update selector for Slack"); Discord's was
 * commented out mid-integration and never restored (`c4d0b9a0e`, "Ongoign
 * discord update"). Both are permanently Shared Link — see `contentFields`.
 */
const HAS_POST_STYLE: DestinationNetwork[] = [
  "facebook",
  "tumblr",
  "linkedin",
  "blogger",
  "wordpress",
  "reddit",
]

/** Permanently Shared Link: no picker in legacy, so no choice to offer. */
const SUMMARY_ONLY: DestinationNetwork[] = ["discord", "slack"]

/*
 * NOT A POST, SO NONE OF THE POST CONTROLS. The Output Feed republishes each
 * item into an RSS document we host; legacy's `FeedOptions.jsx` renders its
 * own four fields and never calls the shared content or affix getters, so the
 * title / body / url / photo switches and both affixes have nothing to act on.
 */
const NO_POST_CONTROLS: DestinationNetwork[] = ["feed"]

/** The two whose post style changes the field set, not just the styling. */
const STYLE_SWITCHES_CONTENT: DestinationNetwork[] = ["facebook", "linkedin"]

/** Section id -> its validation keys, for the tab's error badge. */
export const SOCIAL_ERROR_KEYS: Record<string, string[]> = {
  "post-options": [
    "postTitle",
    "postBody",
    "postUrl",
    "postImages",
    "postQrCode",
    "postDarkQR",
    "prefixText",
    "prefixSpace",
    "postfixText",
    "postfixSpace",
    "includeSummaryTitle",
    "includeSummaryBody",
    "thumbUrl",
    "numItems",
    "staticTitle",
    "name",
    "activeFlag",
    "postStyle",
    "allowCropping",
    "postAction",
    "disable_comment",
    "disable_duet",
    "disable_stitch",
    "disable_music",
    "brand_organic_toggle",
    "brand_content_toggle",
    "privacy_status",
    "license",
    "category",
    "playLists",
    "embeddable",
    "made_for_kids",
    "notify_subs",
    "inputSourceMapping",
  ],
  links: ["pdID", "saID", "urlAppend"],
  advanced: ["postPrivacy", "autoSentiment", "postAsRetweet", "truncationLength"],
}

/**
 * The shortener select writes two ids, not one: picking a custom domain sets
 * `pdID` and clears `saID`, and picking a shortener does the reverse. Without
 * this the Links tab could never read as dirty from a domain change.
 */
export const SOCIAL_COMPOSITE_PATHS: Record<string, string[]> = {
  "social.saID": ["saID", "pdID"],
}

/* ───────────────────────────────────────────────────────── post options ── */

/**
 * PARAM ROOT. The destination screen writes `social.params`; one automation's
 * override of it writes `output.paParams`. Legacy draws the same distinction
 * with `fieldMapping`.
 */
export const PARAM_ROOT = {
  destination: "social.params",
  override: "output.paParams",
} as const

/**
 * Point a set of param fields at the record the screen is actually editing.
 *
 * The two screens render the SAME controls over two records — which networks
 * get which fields is one rule, not two — so the fields are built once against
 * the effective values and retargeted here, rather than every literal carrying
 * a prefix argument.
 *
 * `overrides` is the set of keys this automation has taken over. Only those
 * are badged: the locked product rule is that the default goes unmarked and
 * the exception carries the badge, which is the same call that leaves instant
 * posting unmarked and badges queued.
 */
function retarget(
  fields: SettingsField[],
  root: string,
  overrides?: Set<string>
): SettingsField[] {
  if (root === PARAM_ROOT.destination && !overrides) return fields

  return fields.map((field) => {
    if (!field.name?.startsWith(`${PARAM_ROOT.destination}.`)) return field

    const key = field.name.slice(PARAM_ROOT.destination.length + 1)
    const next: SettingsField = { ...field, name: `${root}.${key}` }

    if (overrides?.has(key)) {
      next.paramState = "override"
      next.paramStateHint = "This automation uses its own value here. The destination's own setting applies everywhere else."
    }

    return next
  })
}

/**
 * WHICH CONTENT CONTROLS A DESTINATION SHOWS, which legacy decides on the post
 * style rather than on the network alone.
 *
 * Facebook and LinkedIn switch the whole set: a Status Update carries the
 * item's title, body, url and photo, and a Shared Link carries none of them —
 * it offers the two summary switches instead, which say what goes in the
 * message ABOVE the link card. `FacebookOptions.jsx:31`,
 * `LinkedInOptions.jsx:31`.
 *
 * That is not only fidelity. Meta ignores an API-supplied title, description
 * and picture on a link post — the 2017 Graph API change — so in Shared Link
 * those four controls could not do anything, and the message above the card is
 * the only thing left to decide.
 *
 * SHARED LINK IS THE DEFAULT, in legacy's own `case POST_SHARED: default:`, so
 * a destination with nothing stored gets the summary pair rather than the four.
 *
 * Everything else keeps the four, and Discord and Slack keep only the pair —
 * see `SUMMARY_ONLY`.
 */
function contentFields(
  network: DestinationNetwork,
  params: LegacyRecord,
  constants: Constants,
  toggle: (
    key: string,
    id: string,
    label: string,
    hint?: string
  ) => SettingsField
): SettingsField[] {
  const summary = [
    toggle(
      "includeSummaryTitle",
      "include-summary-title",
      "Include the title above the link",
      "This text becomes the message of the post, with the link attached below it."
    ),
    toggle(
      "includeSummaryBody",
      "include-summary-body",
      "Include the body above the link",
      "This text becomes the message of the post, with the link attached below it."
    ),
  ]
  const postTitle = toggle(
    "postTitle",
    "post-title",
    "Post title",
    "The item title appears as part of the post."
  )
  const postBody = toggle(
    "postBody",
    "post-body",
    "Post body",
    "Posts the item's body content. Which part - summary or full - comes from the automation's Advanced settings."
  )
  const postUrl: SettingsField = {
    ...toggle(
      "postUrl",
      "post-url",
      "Post URL",
      "Posts the shortened link to the item."
    ),
    /*
     * Legacy hangs this off the Post URL fieldset for Instagram Business and
     * nothing else (`SocialPanelModule.js:431`, gated on
     * `POST_SVC_INSTAGRAM_BUSINESS`). Transcribed verbatim — it is existing
     * production copy, not ours to reword.
     *
     * TONE DIFFERS FROM LEGACY, DELIBERATELY: legacy draws it as a
     * `service-warning` with a triangle. It is `help` here because it is a
     * permanent fact about the platform rather than a state the user can
     * resolve, and an amber warning that never clears is the alarm-fatigue
     * pattern `warning`'s own doc comment warns against. Flagged for Emily —
     * one word changes it back.
     */
    ...(network === "instagramBusiness"
      ? {
          help: "Instagram does not support links on their platform. Posted URLs will display as plain text.",
        }
      : {}),
  }
  const postImages = toggle(
    "postImages",
    "post-images",
    "Post photo",
    "Makes a full-size photo post with the title and link as the message. Photo posts do not include the item summary."
  )
  const content = [postTitle, postBody, postUrl, postImages]

  if (SUMMARY_ONLY.includes(network)) return summary

  /* X puts Post URL last, directly above the QR code that carries the same
     link (`qrCodeFields`), which is the order `TwitterOptions.jsx` settled on. */
  if (network === "twitter") return [postTitle, postBody, postImages, postUrl]

  if (STYLE_SWITCHES_CONTENT.includes(network)) {
    return choice(params.postStyle) === String(constants.POST_TEXT)
      ? content
      : summary
  }

  return content
}

/**
 * X's QR code for the post's link, and the theme of the card it goes out on.
 *
 * NOT A CONTENT SWITCH. The code rides alongside the link rather than
 * replacing it (`postTwitter::applyLinkPolicy`), so a social source that takes
 * over the four switches leaves this pair the automation's to set, as
 * `TwitterOptions.jsx` does under its input-override warning.
 *
 * The theme exists only while codes are on: with none there is no card to
 * theme. Two named choices rather than a switch, because "off" does not say
 * what you get - both are themes. Unset reads as dark, which is what
 * `postTwitter::DEFAULT_PARAMS` posts for it.
 *
 * The copy is legacy's `HelpButton` text, split by this module's hint/help
 * rule: where the code goes is background, so it is the (i); that a video post
 * drops the code changes what is published, so it is the visible line.
 */
function qrCodeFields(
  params: LegacyRecord,
  toggle: (
    key: string,
    id: string,
    label: string,
    hint?: string
  ) => SettingsField
): SettingsField[] {
  const code: SettingsField = {
    ...toggle(
      "postQrCode",
      "post-qr-code",
      "Add a QR code for the link",
      "Turn this on to add a scannable QR code for the link. We put it in the corner of your post's image, or post it as an image of its own when there isn't one."
    ),
    help: "Posts carrying a video can't include one, because X won't accept a video and an image together.",
  }

  if (!code.checked) return [code]

  /* Legacy's empty string counts as unset too, so `choice`, not `??`. */
  const dark = choice(params.postDarkQR) === undefined || flag(params.postDarkQR)

  return [
    code,
    {
      id: "post-dark-qr",
      name: `${PARAM_ROOT.destination}.postDarkQR`,
      label: "QR card theme",
      kind: "segmented",
      options: [
        { value: "1", label: "Dark mode" },
        { value: "0", label: "Light mode" },
      ],
      value: dark ? "1" : "0",
      hint: [
        "When a post has no image of its own, we build one: the destination's domain, your post's title, and the QR code.",
        "Choose whether that card is built dark or light. It changes nothing about posts that already have an image — the code is added to the corner of those, and takes no background of its own.",
      ],
      dependsOn: "post-qr-code",
    },
  ]
}

/**
 * The eight that every destination carries, in §6's order — except that the
 * first four are the post style's to decide (`contentFields`). The prefix and
 * postfix pairs are unconditional: legacy's style switch only ever covered the
 * content controls, and those two render outside it.
 */
function commonPostOptions(
  network: DestinationNetwork,
  params: LegacyRecord,
  constants: Constants
): SettingsField[] {
  if (NO_POST_CONTROLS.includes(network)) return []

  const toggle = (
    key: string,
    id: string,
    label: string,
    /* The (i) beside the label. Long-form background belongs in a tooltip;
       anything that silently changes what gets PUBLISHED is `help`, a visible
       line, because a tooltip is hover and focus only and touch has no route
       to it at all. That is why the affix order below is help, not hint. */
    hint?: string
  ): SettingsField => ({
    id,
    name: `${PARAM_ROOT.destination}.${key}`,
    label,
    kind: "toggle",
    checked: flag(params[key]),
    ...(hint ? { hint } : {}),
  })
  const input = (
    key: string,
    id: string,
    label: string,
    placeholder?: string,
    help?: string
  ): SettingsField => ({
    id,
    name: `${PARAM_ROOT.destination}.${key}`,
    label,
    kind: "input",
    value: text(params[key]),
    ...(placeholder ? { placeholder } : {}),
    ...(help ? { help } : {}),
  })

  /*
   * THE PICKER LEADS, because it decides what the rows under it mean — and
   * on the networks that have one it decides which rows exist at all, since
   * `contentFields` returns the summary pair or the four switches off the same
   * value. Built here rather than in `networkPostOptions` for that reason:
   * appended there it came out below Active, at the bottom of the card.
   */
  const style: SettingsField[] = HAS_POST_STYLE.includes(network)
    ? [
        {
          id: "post-style",
          name: `${PARAM_ROOT.destination}.postStyle`,
          label: "Post style",
          kind: "segmented",
          options: postStyleOptions(network, constants),
          value: choice(params.postStyle),
          hint: "A link post sends the item as a link card, with its title and summary. A text or status post sends the message on its own.",
        },
      ]
    : []

  /*
   * Marked as governed only where there IS a picker above them. Discord and
   * Slack show the summary pair with no choice over it, so indenting those rows
   * would point at a control that is not on the screen.
   */
  const governed = (list: SettingsField[]): SettingsField[] =>
    style.length ? list.map((f) => ({ ...f, dependsOn: "post-style" })) : list

  return [
    ...style,
    ...governed(contentFields(network, params, constants, toggle)),
    ...(network === "twitter" ? qrCodeFields(params, toggle) : []),
    /* Each affix carries its own space flag, as legacy's panel does. Split
       into a sibling row they read as two unrelated settings, and the flag
       loses the only context that explains it - so it is a checkbox under the
       input rather than a row of its own. See `pairedCheck`. */
    {
      ...input(
        "prefixText",
        "prefix-text",
        "Begin posts with",
        /* An EXAMPLE, not a repeat of the label - a placeholder is not a
           label, and repeating it tells the reader nothing. */
        "e.g. Sponsored:"
      ),
      /* `postAccount.php:4565` rejects a hashtag here exactly as
         `Input::validate` does on the feed, so the field says so at the same
         moment. Legacy only ever showed the rule on the feed screen. */
      ...affixHashtagFault(
        text(params.prefixText),
        "Hashtags are set on the automation, not the destination."
      ),
      pairedCheck: {
        id: "prefix-space",
        name: `${PARAM_ROOT.destination}.prefixSpace`,
        label: "Disable space after prefix",
        checked: flag(params.prefixSpace),
      },
    },
    {
      ...input(
        "postfixText",
        "postfix-text",
        "End posts with",
        "e.g. via @yourbrand"
      ),
      ...affixHashtagFault(
        text(params.postfixText),
        "Hashtags are set on the automation, not the destination."
      ),
      pairedCheck: {
        id: "postfix-space",
        name: `${PARAM_ROOT.destination}.postfixSpace`,
        label: "Disable space before suffix",
        checked: flag(params.postfixSpace),
      },
    },
  ]
}

/** The per-network additions, keyed the way `SocialEditorPanel` switches. */
function networkPostOptions(
  network: DestinationNetwork,
  social: LegacyRecord,
  ctx: SocialSectionsContext,
  params: LegacyRecord
): SettingsField[] {
  const { constants } = ctx
  const fields: SettingsField[] = []

  /* `post-style` USED TO BE BUILT HERE, and it is now the first field in
     `commonPostOptions` instead. It decides which content fields exist, so it
     has to sit above them; appended here it landed at the bottom of the card,
     below Active, four rows under the switches it governs. */

  /* Only the two params here. The feed's name and address belong to the
     destination, not to one automation's override — see `feedIdentity`. */
  if (network === "feed") {
    fields.push(
      {
        id: "num-items",
        name: "social.params.numItems",
        label: "Maximum items in the feed",
        kind: "select",
        options: options([5, 10, 15, 20, 25].map((n) => ({ value: n, label: String(n) }))),
        value: choice(params.numItems),
        width: "xs",
      },
      {
        id: "static-title",
        name: "social.params.staticTitle",
        label: "Static title for items",
        kind: "input",
        value: text(params.staticTitle),
        placeholder: "e.g. Shop our latest arrivals",
        hint: "Optional. When set, every item we publish carries this title instead of its own.",
      }
    )
  }

  if (network === "instagramBusiness") {
    fields.push({
      id: "allow-cropping",
      name: "social.params.allowCropping",
      label: "Allow cropping",
      kind: "toggle",
      checked: flag(params.allowCropping),
      help: "Instagram needs a supported aspect ratio. We crop to fit rather than skip the item.",
    })
  }

  if (network === "gmyBusiness") {
    fields.push(
      {
        id: "gmb-post-action",
        name: "social.params.gmb.postAction",
        label: "Button on the post",
        kind: "select",
        options: options([
          { value: constants.ACTION_LEARNMORE, label: "Learn more" },
          { value: constants.ACTION_SIGNUP, label: "Sign up" },
          { value: constants.ACTION_SHOP, label: "Buy" },
          { value: constants.ACTION_ORDER, label: "Order online" },
          { value: constants.ACTION_BOOK, label: "Book" },
        ]),
        value: choice(readPath(params, "gmb.postAction")),
      },
      {
        id: "gmb-url",
        name: "social.params.gmb.url",
        label: "Where the button goes",
        kind: "input",
        value: text(readPath(params, "gmb.url")),
        placeholder: "https://example.com/offers",
        help: "Leave empty to send people to the item we posted.",
      }
    )
  }

  if (network === "tiktok") {
    fields.push(
      {
        id: "disable-comment",
        name: "social.params.disable_comment",
        label: "Turn off comments",
        kind: "toggle",
        checked: flag(params.disable_comment),
      },
      {
        id: "disable-duet",
        name: "social.params.disable_duet",
        label: "Turn off duets",
        kind: "toggle",
        checked: flag(params.disable_duet),
      },
      {
        id: "disable-stitch",
        name: "social.params.disable_stitch",
        label: "Turn off stitches",
        kind: "toggle",
        checked: flag(params.disable_stitch),
      },
      {
        id: "disable-music",
        name: "social.params.disable_music",
        label: "Turn off music on photo posts",
        kind: "toggle",
        checked: flag(params.disable_music),
      },
      {
        id: "brand-organic",
        name: "social.params.brand_organic_toggle",
        label: "These posts promote my own brand",
        kind: "toggle",
        checked: flag(params.brand_organic_toggle),
        hint: 'TikTok shows a "Promotional content" label on the video.',
      },
      {
        id: "brand-content",
        name: "social.params.brand_content_toggle",
        label: "These posts are a paid partnership",
        kind: "toggle",
        checked: flag(params.brand_content_toggle),
        hint: 'TikTok shows a "Paid partnership" label. Required when someone paid you for the post.',
        /* TikTok requires the label to be shown, so say which one the current
           pair produces — legacy prints the same sentence under the two. */
        help: disclosureLabel(params),
      }
    )
  }

  if (network === "youtube") {
    fields.push(
      {
        id: "privacy-status",
        name: "social.params.privacy_status",
        label: "Privacy setting",
        kind: "segmented",
        options: [
          { value: "public", label: "Public" },
          { value: "private", label: "Private" },
          { value: "unlisted", label: "Unlisted" },
        ],
        value: choice(params.privacy_status),
      },
      {
        id: "license",
        name: "social.params.license",
        label: "License",
        kind: "segmented",
        options: [
          { value: "youtube", label: "Standard YouTube License" },
          { value: "creativeCommon", label: "Creative Commons" },
        ],
        value: choice(params.license),
      },
      {
        id: "category",
        name: "social.params.category",
        label: "Category",
        kind: "select",
        options: youTubeCategories(social, ctx),
        value: choice(params.category),
        help: "YouTube uses this to decide who gets recommended the video.",
      },
      {
        id: "playlists",
        name: "social.params.playLists",
        label: "Playlists",
        kind: "tags",
        /* The chips are matched against option LABELS, not values, so the
           picked set is titles. */
        values: (Array.isArray(params.playLists) ? params.playLists : []).map(
          (playlist: LegacyRecord) => text(playlist?.title)
        ),
        options: youTubePlaylists(social, ctx),
        placeholder: "Choose a playlist",
        help: "Optional. Every video we post is added to the playlists you pick.",
        emptyLabel: "This channel has no playlists",
        allPickedLabel: "You've selected all playlists",
        span: "full",
      },
      {
        id: "embeddable",
        name: "social.params.embeddable",
        label: "Make videos embeddable",
        kind: "toggle",
        checked: flag(params.embeddable),
      },
      {
        id: "made-for-kids",
        name: "social.params.made_for_kids",
        label: "Videos are made for kids",
        kind: "toggle",
        checked: flag(params.made_for_kids),
      },
      {
        id: "notify-subs",
        name: "social.params.notify_subs",
        label: "Notify subscribers",
        kind: "toggle",
        checked: flag(params.notify_subs),
      }
    )
  }

  if (network === "snapchat") {
    fields.push({
      id: "input-source-mapping",
      name: "social.params.inputSourceMapping",
      label: "Which post type should we use?",
      help: "The default follows whatever the source item already was.",
      kind: "segmented",
      options: options([
        { value: constants.ITEM_SOURCE_FILTER, label: "Original format" },
        { value: constants.ITEM_SOURCE_REEL, label: "Spotlight" },
        { value: constants.ITEM_SOURCE_STORY, label: "Story" },
      ]),
      value: choice(params.inputSourceMapping),
    })
  }

  return fields
}

/**
 * The Output Feed's address and name, which lead its own screen in legacy's
 * order. Legacy's output-mapped panel shows neither, so the override does not
 * either: the name is `social.name`, which `retarget` would leave pointing at
 * the destination, and the address is fixed by the destination's external id.
 */
function feedIdentity(
  social: LegacyRecord,
  ctx: SocialSectionsContext
): SettingsField[] {
  return [
    {
      id: "feed-url",
      name: "social.params.url",
      label: "Feed URL",
      kind: "static",
      /* Built rather than stored: `FeedOptions` composes it from the feed
         host and the output's external id every render. */
      staticValue: `${ctx.feedUrlBase}/${text(social.externalID)}.xml`,
      span: "full",
      help: "We generate this. Share it, or point another reader at it.",
    },
    {
      id: "feed-name",
      name: "social.name",
      label: "Feed name",
      kind: "input",
      value: text(social.name),
      required: true,
    },
  ]
}

/** Which two a network calls its link card and its plain post. */
function postStyleOptions(
  network: DestinationNetwork,
  constants: Constants
): SettingsOption[] {
  switch (network) {
    case "tumblr":
      return options([
        { value: constants.TUMBLR_POST_TEXT, label: "Text" },
        { value: constants.TUMBLR_POST_LINK, label: "Link" },
      ])
    case "blogger":
      return options([
        { value: constants.BLOGGER_POST_TEXT, label: "Text" },
        { value: constants.BLOGGER_POST_LINK, label: "Link" },
      ])
    case "wordpress":
      return options([
        { value: constants.WORDPRESSOUTPUT_POST_TEXT, label: "Text" },
        { value: constants.WORDPRESSOUTPUT_POST_LINK, label: "Link" },
      ])
    case "reddit":
      return options([
        { value: constants.REDDIT_POST_LINK, label: "Link" },
        { value: constants.REDDIT_POST_IMAGE, label: "Image" },
      ])
    default:
      /* Facebook, LinkedIn and Discord share the generic pair. */
      return options([
        { value: constants.POST_SHARED, label: "Shared link" },
        { value: constants.POST_TEXT, label: "Status update" },
      ])
  }
}

/**
 * The label TikTok will put on the post, given the two disclosure switches.
 *
 * NOT LEGACY'S MAPPING, DELIBERATELY. `getTikTokFields` reads
 * `brand_content_toggle` into a local called `yourBrand` and
 * `brand_organic_toggle` into `partnerBrands`, labels its two switches to
 * match, and derives the sentence from those names — which is the opposite way
 * round from TikTok's own API, where `brand_organic_toggle` is your own brand
 * and `brand_content_toggle` is the paid partnership. §6's labels follow the
 * API; so does this.
 */
function disclosureLabel(params: LegacyRecord): string | undefined {
  const ownBrand = flag(params.brand_organic_toggle)
  const paid = flag(params.brand_content_toggle)

  if (paid) return "These posts will be labelled 'Paid partnership'."
  if (ownBrand) return "These posts will be labelled 'Promotional content'."

  return undefined
}

/**
 * YouTube's categories arrive keyed by social id. Keyed lookup first; legacy
 * reads the first key whatever it is, which is only right because it fetches
 * one channel at a time, so that stays as the fallback rather than the rule.
 */
function youTubeCategories(
  social: LegacyRecord,
  ctx: SocialSectionsContext
): SettingsOption[] {
  const byId = (ctx.categories ?? {})[String(social?.id ?? "")]
  const keys = Object.keys(ctx.categories ?? {})
  const list = byId ?? (keys.length ? ctx.categories[keys[0]] : [])

  return Array.isArray(list) ? options(list) : []
}

function youTubePlaylists(
  social: LegacyRecord,
  ctx: SocialSectionsContext
): SettingsOption[] {
  const list = (ctx.playlists ?? {})[String(social?.id ?? "")] ?? []

  return Array.isArray(list)
    ? list.map((playlist: LegacyRecord) => ({
        value: text(playlist?.id),
        label: text(playlist?.title),
      }))
    : []
}

/* ─────────────────────────────────────────────────────────────── links ── */

/**
 * ONE SELECT, TWO STORED IDS. A shortener sets `saID`, a custom domain sets
 * `pdID`, and choosing either clears the other — so the option values are
 * `shortener-<id>` / `domain-<id>` rather than a bare id, exactly as
 * `HelperPanel.updateField` encodes them. The field is named for `saID`
 * because that is the one §6 names; `SOCIAL_COMPOSITE_PATHS` carries the other
 * half for the dirty check.
 */
function linkTargetOptions(ctx: SocialSectionsContext): SettingsOption[] {
  const list: SettingsOption[] = [
    { value: "shortener-0", label: "Don't shorten links" },
    { value: "shortener-1", label: "dlvr.it" },
  ]

  for (const shortener of ctx.shorteners ?? []) {
    const ssID = parseInt(String(shortener.ssID ?? ""), 10) || 0

    list.push({
      value: `shortener-${shortener.id}`,
      label: ssID > 1
        ? `${ctx.shortenServiceName(ssID)} - ${text(shortener.name)}`
        : text(shortener.name),
    })
  }

  for (const domain of ctx.domains ?? []) {
    list.push({ value: `domain-${domain.id}`, label: text(domain.domain) })
  }

  return list
}

function linksSection(
  social: LegacyRecord,
  ctx: SocialSectionsContext
): SettingsSection {
  const domainId = parseInt(String(social.pdID ?? ""), 10) || 0
  const shortenerId = parseInt(String(social.saID ?? ""), 10) || 0

  return {
    id: "links",
    label: SECTION_LABEL.links,
    fields: [
      {
        id: "sa-id",
        name: "social.saID",
        label: "Link shortening service or custom domain",
        kind: "select",
        options: linkTargetOptions(ctx),
        /* A custom domain wins when one is set; `pa_saID` is NOT NULL and
           always carries an explicit 0, so the fallback is always valid. */
        value: domainId > 1 ? `domain-${domainId}` : `shortener-${shortenerId}`,
        /* Only the advanced view lets a route choose its own, which then
           comes first; the simple view's routes always leave it to here. */
        hint: ctx.advancedView
          ? "Items posted through this destination use this shortener unless their route has one of its own. Choosing one other than ours means we cannot report click stats for it."
          : "Every item posted through this destination uses this shortener. Choosing one other than ours means we cannot report click stats for it.",
      },
      {
        id: "url-append",
        name: "social.urlAppend",
        label: "Analytics",
        kind: "textarea",
        value: text(social.urlAppend),
        placeholder: "utm_source=[desttype]",
        help: "Add analytics to your links for enhanced reporting from your site analytics service.",
        /* Legacy prints this table under the field. It is a lookup the reader
           consults while typing, so it goes behind the glyph rather than into
           a permanent block — the `reference` kind is the route screen's
           treatment, and §6 does not give this tab one. */
        hint: [
          "[feedurl] — if your source is a feed, that feed's URL",
          "[sourcename] — the name of your input (URL-encoded)",
          "[desttype] — where the item was posted, e.g. facebook",
          "[destname] — the account name on your output (URL-encoded)",
          "[routename] — the name of your automation (URL-encoded)",
        ],
        span: "full",
      },
    ],
  }
}

/* ──────────────────────────────────────────────────────────── advanced ── */

function advancedSection(
  network: DestinationNetwork,
  social: LegacyRecord,
  ctx: SocialSectionsContext,
  params: LegacyRecord
): SettingsSection {
  /* No "Collect social stats" switch, though legacy had one: nothing on the
     server has read `socialStats` since 2015. Whether a destination's per-post
     stats are collected is down to a config flag, the plan and an admin-only
     override. */
  const fields: SettingsField[] = []

  if (network === "twitter") {
    fields.push({
      id: "truncation-length",
      name: "social.params.truncationLength",
      label: "Text truncation length",
      kind: "segmented",
      options: options([
        { value: 280, label: "280 characters" },
        { value: 140, label: "140 characters" },
      ]),
      value: choice(params.truncationLength) ?? "280",
      help: "280 is the default.",
    })
  }

  if (network === "stocktwits") {
    fields.push({
      id: "auto-sentiment",
      name: "social.params.autoSentiment",
      label: "Auto sentiment",
      kind: "toggle",
      help: "Posts an item as bullish or bearish when it has a bullish or bearish category, or a #bullish or #bearish hashtag, and leaves that tag out of the post. Otherwise it posts as neutral.",
      checked: flag(params.autoSentiment),
    })
  }

  if (network === "linkedin") {
    fields.push({
      id: "post-privacy",
      name: "social.params.postPrivacy",
      label: "Post privacy",
      kind: "segmented",
      options: options([
        { value: ctx.constants.LI_PRIV_CONTACTS, label: "Contacts" },
        { value: ctx.constants.LI_PRIV_LOGGEDIN, label: "Logged in" },
        { value: ctx.constants.LI_PRIV_PUBLIC, label: "Public" },
      ]),
      value: choice(params.postPrivacy),
    })
  }

  return { id: "advanced", label: SECTION_LABEL.advanced, fields }
}

/* ────────────────────────────────────────────────────────── the builder ── */

/**
 * A destination's own settings — `social.params`, which apply to every
 * automation posting to this output.
 *
 * The per-automation override of the same params is a different record on a
 * different surface (`destination-override.tsx`, reached from
 * `OutputConfigModal`), so it is deliberately not built here.
 */
export function buildSocialSections(
  social: LegacyRecord | null | undefined,
  ctx: SocialSectionsContext
): SettingsSection[] {
  const network = destinationNetwork(social)

  if (!social || social.id === undefined || !network) return []

  const params: LegacyRecord = social.params ?? {}
  const sections: SettingsSection[] = [
    {
      id: "post-options",
      label: SECTION_LABEL["post-options"],
      /* `activeLast` puts the switch at the foot of the card. Declared here in
         legacy's own order, which has it before the per-network additions. */
      fields: activeLast([
        ...(network === "feed" ? feedIdentity(social, ctx) : []),
        ...commonPostOptions(network, params, ctx.constants),
        /* No Active switch here (Emily, 2026-10-01): a destination is
           paused and resumed from its list, not its settings. */
        ...networkPostOptions(network, social, ctx, params),
      ]),
    },
  ]

  if (!POST_OPTIONS_ONLY.includes(network)) {
    sections.push(linksSection(social, ctx))
  }

  if (HAS_ADVANCED.includes(network)) {
    sections.push(advancedSection(network, social, ctx, params))
  }

  /*
   * `errorMapping` is "params" on this screen, so the server reports
   * `params.postTitle` where the field's path says `social.params.postTitle` —
   * stripping the `social.` prefix lands on the same key either way.
   */
  return attachErrors(sections, { tree: ctx.validation, prefix: "social" })
}

/* ──────────────────────────────────────────────────────────── the override ── */

/** Source types whose own settings decide how a post looks (H5). */
const SOURCE_OWNS_APPEARANCE = ["twitter", "instagram", "facebook", "tiktok", "youtube"]

/** The four content switches a social source takes over. */
/*
 * The fields a social source takes over, by id.
 *
 * THE SUMMARY PAIR IS HERE FOR THE SAME REASON THE FOUR ARE. On a link-style
 * network `contentFields` returns `include-summary-*` INSTEAD of the four, and
 * Shared Link is Facebook's default — so with only the four listed, the H5
 * filter below found nothing to replace on exactly the networks where it
 * mattered, the message never appeared, and a Facebook destination fed by a
 * social source left both summary switches editable.
 *
 * `post-style` is deliberately NOT here, and that is a question rather than a
 * ruling: the picker IS how posts look, so a source that owns the appearance
 * arguably owns it too — but it is also the only control deciding which set of
 * fields exists. Flagged for Emily rather than inferred.
 */
const CONTENT_SWITCHES = [
  "post-title",
  "post-body",
  "post-url",
  "post-images",
  "include-summary-title",
  "include-summary-body",
]

export type OverrideSectionsContext = SocialSectionsContext & {
  /**
   * The automation's source type, when it is a social one.
   *
   * H5 IS A DATA RULE, which is why it lives here and `destination-override`
   * has no `sourceType` prop: a social source composes its own post text and
   * media, so the four content switches on this screen would not be read. The
   * section builder swaps them for one message; the component renders whatever
   * it is handed.
   */
  sourceType?: string
  /** Where that message sends the reader. */
  scope?: "automation" | "route"
}

/**
 * Which params this automation has actually taken over.
 *
 * `paParams` is null while it inherits, and the first edit seeds it with a
 * copy of the destination's whole params object — so presence is not override,
 * and comparing against the destination is the only way to tell which keys
 * really differ. `OutputConfigModal.trimParams` does the same comparison on
 * the way out, which is what keeps the two in step.
 */
function overriddenKeys(output: LegacyRecord, social: LegacyRecord): Set<string> {
  const pa = output?.paParams
  const base: LegacyRecord = social?.params ?? {}
  const keys = new Set<string>()

  if (!pa || typeof pa !== "object") return keys

  for (const [key, value] of Object.entries(pa)) {
    const mine = typeof value === "object" && value !== null ? JSON.stringify(value) : String(value)
    const theirs =
      typeof base[key] === "object" && base[key] !== null
        ? JSON.stringify(base[key])
        : String(base[key])

    if (mine !== theirs) keys.add(key)
  }

  return keys
}

/**
 * One automation's settings for one destination — legacy's `OutputConfigModal`.
 *
 * The same controls the destination screen shows, over `output.paParams`
 * falling back to the destination's own params for anything not overridden,
 * which is exactly what `getParamValue` does.
 *
 * NO LINKS TAB. The shortener and the analytics string apply to every link the
 * destination posts, so they are not an automation's to override —
 * `getUrlAppend` returns null as soon as `fieldMapping` names an output, and
 * `SocialConfigButtons` drops the tab when handed a handler instead of a link.
 */
export function buildOverrideSections(
  output: LegacyRecord | null | undefined,
  social: LegacyRecord | null | undefined,
  ctx: OverrideSectionsContext
): SettingsSection[] {
  const network = destinationNetwork(social)

  if (!output || !social || social.id === undefined || !network) return []

  /* The effective value of every param: the override where there is one, the
     destination's own everywhere else. */
  const params: LegacyRecord = {
    ...(social.params ?? {}),
    ...(output.paParams ?? {}),
  }
  const overrides = overriddenKeys(output, social)
  /* A destination with no post controls has nothing for the source to take
     over, so the message would explain away a control that is not there. */
  const sourceOwns = Boolean(
    ctx.sourceType &&
      SOURCE_OWNS_APPEARANCE.includes(ctx.sourceType) &&
      !NO_POST_CONTROLS.includes(network)
  )

  let postOptions = [
    ...commonPostOptions(network, params, ctx.constants),
    ...networkPostOptions(network, social, ctx, params),
  ]

  if (sourceOwns) {
    /*
     * H5. A social source builds its own text and media, so these four are not
     * consulted for it. One message replaces them rather than four disabled
     * switches, because a disabled control invites the reader to work out how
     * to enable it and there is nothing to find.
     */
    postOptions = [
      {
        id: "source-owns-appearance",
        label: "How posts look",
        kind: "static",
        staticValue: `This ${ctx.scope ?? "automation"} pulls from a social account, which brings its own text and media. Change how posts look in the ${ctx.scope ?? "automation"}'s own settings.`,
        span: "full",
      },
      ...postOptions.filter((field) => !CONTENT_SWITCHES.includes(field.id)),
    ]
  }

  const sections: SettingsSection[] = [
    {
      id: "post-options",
      label: SECTION_LABEL["post-options"],
      /* Already last here, and wrapped so it stays last if anything is ever
         appended after it. */
      fields: activeLast([
        ...retarget(postOptions, PARAM_ROOT.override, overrides),
        /* No "Post to this output" switch (Emily, 2026-10-01), for the same
           reason as the pages' Active: on and off is not a setting here. */
      ]),
    },
  ]

  if (HAS_ADVANCED.includes(network) || sourceOwns) {
    const advanced = advancedSection(network, social, ctx, params)

    sections.push({
      ...advanced,
      fields: retarget(advanced.fields, PARAM_ROOT.override, overrides),
    })
  }

  /* `errorMapping` is "paParams" here, so the server reports
     `paParams.postTitle` against a field named `output.paParams.postTitle`. */
  return attachErrors(sections, { tree: ctx.validation, prefix: "output" })
}
